import type { ProductDocument, VariationKey } from 'models/product';

export type VariationOption = {
  id: string;
  values: { key: string; value: string | number }[];
};

// Past this many listings a group reads as a wall of chips rather than a choice.
export const CROWDED_VARIATION_COUNT = 10;

export function isSizeKey(key: string): boolean {
  return key === 'letter_size' || key === 'number_size';
}

/**
 * Whether sold-out values should be dropped from the axes instead of dimmed.
 *
 * A group with no size axis is picked purely by colorway, and clearance feeds
 * leave behind dozens of colorways that never come back in stock — they bury
 * the ones that can still be bought. The same holds once a group runs past
 * `CROWDED_VARIATION_COUNT` listings, whatever its axes are.
 */
export function shouldHideSoldOutValues(
  variationKeys: VariationKey[],
  optionCount: number
): boolean {
  if (variationKeys.length === 0) return false;
  if (!variationKeys.some((axis) => isSizeKey(axis.key))) return true;
  return optionCount > CROWDED_VARIATION_COUNT;
}

/**
 * Values carried by at least one purchasable listing, per axis.
 *
 * Availability is read across the whole group rather than against the current
 * selections, so hiding only ever drops a value that cannot be bought in any
 * combination — a value that is merely out of stock for the current pick stays
 * visible and dimmed.
 */
export function getInStockValues(
  options: VariationOption[],
  fittingItems: ProductDocument[]
): Map<string, Set<string>> {
  const itemsById = new Map(fittingItems.map((item) => [item.id, item]));
  const byKey = new Map<string, Set<string>>();

  for (const option of options) {
    const item = itemsById.get(option.id);
    if (!item || item.out_of_stock) continue;

    for (const { key, value } of option.values) {
      if (!byKey.has(key)) byKey.set(key, new Set());
      byKey.get(key)!.add(String(value));
    }
  }

  return byKey;
}
